import { Splide, SplideProps, SplideTrack } from '@splidejs/react-splide'
import merge from 'lodash/merge'
import React, { forwardRef, Ref } from 'react'
import styled, { css } from 'styled-components'

import SVGAngleRight from '@Assets/SVG/AngleRight'

import { IsOverStyleExtension } from './extensions/IsOverStyleExtension'
import { StyledSlider } from './StyledSlider'

const CustomSlider = styled(StyledSlider).withConfig(
  { shouldForwardProp: prop => !['isVertical', 'noArrows', 'isFullscreen'].includes(prop) },
)<{ isVertical: boolean, noArrows: boolean }>(({
  isVertical,
  noArrows,
}) => css`
  ${isVertical && css`
    padding: 35px 0;
  `};

  ${noArrows && css`
    padding-top: 0;
    padding-bottom: 0;
  `};

  .splide__arrows {
    display: none;
  }

  // This is a tiny hack to only show the arrows once the slider has actually instantiated. Clever, eh?
  &[aria-roledescription] .splide__arrows {
    display: block;
  }

  .splide__arrow {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 0;
    background: rgba(236, 236, 236, 0.8);
    width: 38px;
    height: 38px;
    opacity: .9;

    &--hidden {
      display: none;
    }

    svg {
      height: unset;
    }
  }

  .splide__slide {
    cursor: pointer;
  }
`)

const defaultProps: SplideProps = {
  extensions: {
    IsOverStyleExtension,
  },
  options: {
    arrowsPath: 'm13.5 7.01 13 13m-13 13 13-13 m13.5 7.01 13 13m-13 13 13-13',
  },
}

interface SliderProps extends SplideProps {
  assets?: number
  hideLeftArrow?: boolean
  isFullscreen?: boolean
  ref?: Ref<Splide>
}

export const Slider: React.FC<SliderProps> = forwardRef(({
  children,
  ...componentProps
}, ref: Ref<Splide>) => {
  const disablePagination = componentProps?.assets ? componentProps.assets <= 6 : false
  const props = merge({}, defaultProps, componentProps, { ...(disablePagination ? { options: { arrows: false } } : {}) })

  const isVertical = props?.options?.direction === 'ttb'
  return (
    <CustomSlider
      {...props}
      hasTrack={false}
      isVertical={isVertical}
      noArrows={disablePagination}
      ref={ref}
    >
      <SplideTrack>
        {children}
      </SplideTrack>

      <div className="splide__arrows" hidden={props.options?.arrows === false}>
        <button
          className={`splide__arrow splide__arrow--prev ${componentProps.hideLeftArrow && 'splide__arrow--hidden'}`}
          type="button"
        >
          <SVGAngleRight />
        </button>
        <button className="splide__arrow splide__arrow--next" type="button">
          <SVGAngleRight />
        </button>
      </div>
    </CustomSlider>
  )
})
